import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { programGridZoomSearchSchema } from "src/contexts/ProgramGridZoomContext";
import { loaderHandler } from "src/lib/loaderHandler";
import { CoachAthleteProgramDetailsPage } from "src/pages/CoachAthleteProgramDetailsPage";
import { athleteProfileQueryOptions } from "src/queries/athletes";
import { createPrivatePartnershipProgramsRepository } from "src/repositories/programs/createPrivatePartnershipProgramsRepository";
import { exerciseCategoriesQueryOptions } from "src/state/exerciseCategories";
import { exerciseMovementPatternsQueryOptions } from "src/state/exerciseMovementPatterns";
import { exerciseMuscleGroupsQueryOptions } from "src/state/exerciseMuscleGroups";
import {
  CommentsContextProvider,
  disabledCommentsContext,
  enabledCommentsContext,
} from "src/workouts/CommentsContext";

const searchSchema = z.object({
  comments: z.string().optional(),
  selectedWorkout: z.string().optional(),
  workoutItemId: z.string().optional(),
  zoom: programGridZoomSearchSchema,
});

export const Route = createFileRoute(
  "/_auth/_coach/athletes/$athleteId/programs/$programId/",
)({
  component: RouteComponent,
  ssr: true,
  shouldReload: false,
  validateSearch: searchSchema,
  async loader({ context, params }) {
    return loaderHandler(async () => {
      const athleteProfile = await context.queryClient.query({
        ...athleteProfileQueryOptions(params.athleteId),
        staleTime: "static",
      });
      const programsRepository = createPrivatePartnershipProgramsRepository({
        athleteId: params.athleteId,
        partnershipId: athleteProfile.athlete.partnership.id,
      });
      const dataPromise = Promise.all([
        context.queryClient.query({
          ...programsRepository.detailOptions(params.programId),
          staleTime: "static",
        }),
        context.queryClient.query({
          ...exerciseCategoriesQueryOptions(),
          staleTime: "static",
        }),
        context.queryClient.query({
          ...exerciseMuscleGroupsQueryOptions(),
          staleTime: "static",
        }),
        context.queryClient.query({
          ...exerciseMovementPatternsQueryOptions(),
          staleTime: "static",
        }),
      ]);

      const [program] = await dataPromise;

      return {
        data: {
          program,
        },
        breadcrumb: {
          id: program.id,
          label: program.name,
          href: `/athletes/${params.athleteId}/programs/${params.programId}`,
        },
      };
    });
  },
});

function RouteComponent() {
  const { data } = Route.useLoaderData();

  const athleteUserId = data.program.assignedTo?.id;
  const commentsContext = athleteUserId
    ? enabledCommentsContext(athleteUserId)
    : disabledCommentsContext;

  return (
    <CommentsContextProvider value={commentsContext}>
      <CoachAthleteProgramDetailsPage />
    </CommentsContextProvider>
  );
}
